'use client';

import { useAuth } from '@/hooks/use-auth-rbac';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import useUserStore from '@/states/user-store';
import { Menu, LogOut, Settings } from 'lucide-react';
import { SiteLogo } from '@/components/ui/SiteLogo';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useTranslation } from '@/contexts/LanguageContext';
import { NotificationsDropdown } from '@/components/dashboard/NotificationsDropdown';



export function Header() {
  const pathname = usePathname();
  const { isAuthenticated, user, logout } = useUserStore((state) => state);
  const { logout: handleLogout, loading: isLoggingOut } = useAuth();
  const { t } = useTranslation();

  const navLinks = [
    { href: '/properties', label: t.common.properties },
    { href: '/crypto', label: 'Crypto' },
    { href: '/blog', label: t.common.blog },
    { href: '/agents', label: t.common.agents },
    { href: '/contact', label: t.common.contactUs },
  ];

  // Show login/signup if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <header className="bg-card/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 flex justify-between items-center py-3">
          <Link href="/" className="flex items-center" aria-label="Go to homepage">
            <SiteLogo />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => {
              const isActive = link.href === '/' ? pathname === '/' : pathname.startsWith(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`text-sm relative font-medium transition-colors hover:text-primary ${isActive ? 'text-primary' : 'text-muted-foreground'
                    }`}
                >
                  {link.label}
                  {isActive && (
                    <div className="bg-primary h-[2px] w-full absolute -bottom-1 left-0 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <LanguageSwitcher />
            <Button variant="ghost" asChild>
              <Link href="/login">Login</Link>
            </Button>
            <Button asChild>
              <Link href="/signup">Sign Up</Link>
            </Button>
          </div>

          {/* Mobile menu (unauthenticated) */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="top" className="pt-6 w-full max-h-[80vh] overflow-y-auto border-b">
                <div className="flex justify-between items-center px-4 mb-4">
                  <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                  <LanguageSwitcher />
                </div>
                <nav className="flex flex-col gap-4 mt-2 px-4">
                  {navLinks.map((link) => {
                    const isActive = link.href === '/' ? pathname === '/' : pathname.startsWith(link.href);
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        className={`text-base font-medium transition-colors hover:text-primary ${isActive ? 'text-primary' : 'text-muted-foreground'
                          }`}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </nav>
                <div className="flex flex-col gap-2 mt-6 px-4 pb-4">
                  <Button variant="ghost" asChild>
                    <Link href="/login">Login</Link>
                  </Button>
                  <Button asChild>
                    <Link href="/signup">Sign Up</Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-card/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 flex justify-between items-center py-3">
        <Link
          href="/"
          className="flex items-center"
          aria-label="Go to homepage"
        >
          <SiteLogo />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => {
            const isActive = link.href === '/' ? pathname === '/' : pathname.startsWith(link.href);

            return (
              <Link
                key={link.href}
                href={link.href}
                className={`text-sm relative font-medium transition-colors hover:text-primary ${isActive ? 'text-primary' : 'text-muted-foreground'
                  }`}
              >
                {link.label}
                {isActive && (
                  <div className="bg-primary h-[2px] w-full absolute -bottom-1 left-0 rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <LanguageSwitcher />
          {isAuthenticated && user ? (
            <>
              {/* User Avatar and Menu */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="" alt={`${user.firstName} ${user.lastName}`} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden lg:block">
                    <p className="text-sm font-medium text-foreground">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user.role === 'admin' ? 'Administrator' : user.role === 'agent' ? 'Agent' : 'User'}
                    </p>
                  </div>
                </div>

                {/* Notifications */}
                <NotificationsDropdown />

                {/* Profile/Settings Button */}
                <Button variant="ghost" size="icon" asChild className="hover:bg-accent">
                  <Link href={'/dashboard'} title="Dashboard">
                    <Settings className="h-4 w-4" />
                  </Link>
                </Button>

                {/* Logout Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="hover:bg-accent text-red-500 hover:text-red-600"
                  disabled={isLoggingOut}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">
                    {isLoggingOut ? '...' : t.common.logout}
                  </span>
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button variant="ghost" asChild>
                <Link href="/login">Login</Link>
              </Button>
              <Button asChild>
                <Link href="/signup">Sign Up</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile menu */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="top" className="pt-6 w-full max-h-[80vh] overflow-y-auto border-b">
              <div className="flex justify-between items-center px-4 mb-4">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <LanguageSwitcher />
              </div>
              <nav className="flex flex-col gap-4 mt-2 px-4">
                {navLinks.map((link) => {
                  const isActive = link.href === '/' ? pathname === '/' : pathname.startsWith(link.href);
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      className={`text-base font-medium transition-colors hover:text-primary ${isActive ? 'text-primary' : 'text-muted-foreground'
                        }`}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
              <div className="flex flex-col gap-2 mt-6 px-4 pb-4">
                {isAuthenticated && user ? (
                  <>
                    <div className="flex items-center gap-3 p-4 bg-accent/50 rounded-lg mb-4 border">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src="" alt={`${user.firstName} ${user.lastName}`} />
                        <AvatarFallback className="bg-primary text-primary-foreground font-medium">
                          {user.firstName?.[0]}{user.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="font-medium text-foreground">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.role === 'admin' ? 'Administrator' : user.role === 'agent' ? 'Agent' : 'User'}
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" asChild className="w-full justify-start">
                      <Link href={'/dashboard'}>
                        <Settings className="mr-2 h-4 w-4" />
                        {user.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      className="w-full justify-start text-red-500 hover:text-red-600"
                      disabled={isLoggingOut}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      {isLoggingOut ? 'Logging out...' : t.common.logout}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" asChild>
                      <Link href="/login">Login</Link>
                    </Button>
                    <Button asChild>
                      <Link href="/signup">Sign Up</Link>
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
